import React, { useEffect, useState } from 'react';
import { FileText, ClipboardCheck, Truck, CheckCircle2, Clock, XCircle, ArrowRight, Loader2, Package } from 'lucide-react';
import { documentTrailAPI } from '../../services/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'text-gray-500', bg: 'bg-gray-50', step: 0 },
  quoted: { label: 'Quotation Sent', color: 'text-blue-600', bg: 'bg-blue-50', step: 1 },
  rejected: { label: 'Rejected', color: 'text-red-600', bg: 'bg-red-50', step: -1 },
  accepted_awaiting_oc: { label: 'Accepted — Awaiting OC', color: 'text-indigo-600', bg: 'bg-indigo-50', step: 1 },
  confirmed: { label: 'OC Confirmed', color: 'text-blue-600', bg: 'bg-blue-50', step: 2 },
  ready_to_dispatch: { label: 'Ready to Dispatch', color: 'text-amber-600', bg: 'bg-amber-50', step: 3 },
  dispatched: { label: 'Dispatched', color: 'text-emerald-600', bg: 'bg-emerald-50', step: 4 },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', bg: 'bg-gray-100', step: -1 },
  in_progress: { label: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-50', step: 2 },
  not_found: { label: 'Not Found', color: 'text-gray-400', bg: 'bg-gray-50', step: -1 },
};

const STEPS = [
  { key: 'quotation', label: 'Quotation', icon: FileText },
  { key: 'accepted', label: 'Accepted', icon: CheckCircle2 },
  { key: 'oc', label: 'Order Confirmation', icon: ClipboardCheck },
  { key: 'ready', label: 'Ready to Dispatch', icon: Package },
  { key: 'dispatched', label: 'Dispatched', icon: Truck },
];

export const SalesDocumentTrail = ({ docType, docId }) => {
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!docType || !docId) return;
    setLoading(true);
    documentTrailAPI.getSalesTrail(docType, docId)
      .then(setTrail)
      .catch(() => setTrail(null))
      .finally(() => setLoading(false));
  }, [docType, docId]);

  if (loading) return <div className="flex items-center gap-2 py-3 text-xs text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading trail...</div>;
  if (!trail) return null;

  const lifecycle = STATUS_CONFIG[trail.lifecycle_status] || STATUS_CONFIG.not_found;
  const activeStep = lifecycle.step;

  return (
    <div data-testid="sales-document-trail" className="bg-white border border-gray-100 rounded-xl p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Sales Document Trail</h4>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold ${lifecycle.bg} ${lifecycle.color}`}>
          {activeStep === 4 ? <CheckCircle2 className="w-3.5 h-3.5" /> :
           activeStep === -1 ? <XCircle className="w-3.5 h-3.5" /> :
           <Clock className="w-3.5 h-3.5" />}
          {lifecycle.label}
        </span>
      </div>

      {/* Pipeline Steps */}
      <div className="flex items-center gap-0">
        {STEPS.map((step, idx) => {
          const done = activeStep >= idx;
          const active = activeStep === idx;
          const cancelled = activeStep === -1;
          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                  cancelled ? 'border-gray-200 bg-gray-50' :
                  done ? 'border-emerald-500 bg-emerald-50' :
                  active ? `border-current ${lifecycle.bg}` : 'border-gray-200 bg-gray-50'
                }`}>
                  <step.icon className={`w-4 h-4 ${
                    cancelled ? 'text-gray-300' :
                    done ? 'text-emerald-600' :
                    active ? lifecycle.color : 'text-gray-300'
                  }`} />
                </div>
                <span className={`text-[10px] mt-1.5 font-medium text-center leading-tight ${
                  cancelled ? 'text-gray-400' :
                  done ? 'text-emerald-700' :
                  active ? lifecycle.color : 'text-gray-400'
                }`}>{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 -mt-4 ${
                  cancelled ? 'bg-gray-200' :
                  activeStep > idx ? 'bg-emerald-400' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Document Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        {/* Quotation Card */}
        <div className={`rounded-lg border p-3 ${trail.quotation ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <FileText className={`w-4 h-4 ${trail.quotation ? 'text-blue-600' : 'text-gray-300'}`} />
            <span className="text-[11px] font-semibold text-gray-700">Quotation</span>
          </div>
          {trail.quotation ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-900">{trail.quotation.customer_name}</p>
              <p className="text-[10px] text-gray-500">{trail.quotation.salesman_name} | {trail.quotation.date}</p>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-semibold ${
                  trail.quotation.status === 'Accepted' ? 'bg-emerald-50 text-emerald-700' :
                  trail.quotation.status === 'Sent' ? 'bg-blue-50 text-blue-700' :
                  trail.quotation.status === 'Rejected' ? 'bg-red-50 text-red-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{trail.quotation.status}</span>
                <span className="text-[9px] text-gray-500">₹{Number(trail.quotation.total || 0).toLocaleString()}</span>
              </div>
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">Direct OC — no quotation</p>
          )}
        </div>

        {/* OC Card */}
        <div className={`rounded-lg border p-3 ${trail.oc ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <ClipboardCheck className={`w-4 h-4 ${trail.oc ? 'text-indigo-600' : 'text-gray-300'}`} />
            <span className="text-[11px] font-semibold text-gray-700">Order Confirmation</span>
          </div>
          {trail.oc ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-900">{trail.oc.oc_number}</p>
              <p className="text-[10px] text-gray-500">{trail.oc.customer_name}{trail.oc.project_name ? ` — ${trail.oc.project_name}` : ''}</p>
              <div className="flex items-center gap-2">
                <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-semibold ${
                  trail.oc.status === 'Dispatched' ? 'bg-emerald-50 text-emerald-700' :
                  trail.oc.status === 'Ready to Dispatch' ? 'bg-amber-50 text-amber-700' :
                  trail.oc.status === 'Confirmed' ? 'bg-blue-50 text-blue-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{trail.oc.status}</span>
                <span className="text-[9px] text-gray-500">₹{Number(trail.oc.total || 0).toLocaleString()}</span>
              </div>
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">Not yet created</p>
          )}
        </div>

        {/* Dispatch Card */}
        <div className={`rounded-lg border p-3 ${trail.dispatches?.length > 0 ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Truck className={`w-4 h-4 ${trail.dispatches?.length > 0 ? 'text-emerald-600' : 'text-gray-300'}`} />
            <span className="text-[11px] font-semibold text-gray-700">Dispatches ({trail.dispatches?.length || 0})</span>
          </div>
          {trail.dispatches?.length > 0 ? (
            <div className="space-y-1.5 max-h-24 overflow-y-auto">
              {trail.dispatches.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-[10px]">
                  <div>
                    <span className="text-gray-700">{d.vehicle_number || 'N/A'}</span>
                    {d.lr_number && <span className="text-gray-400 ml-1">LR: {d.lr_number}</span>}
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-semibold ${
                    d.status === 'Delivered' ? 'bg-emerald-50 text-emerald-700' :
                    d.status === 'In Transit' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
                  }`}>{d.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-gray-400 italic">No dispatches yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesDocumentTrail;
